import { useState } from 'react';
import i18nInst from '@shared/i18n';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveStream } from '@nivo/stream';
import { ResponsiveBump } from '@nivo/bump';
import { CHART, monotonePath } from '@shared/ui/chart';
import { nivoTheme } from '@shared/ui/nivoTheme';

// Nivo-powered alternatives to the hand-rolled SoV area chart, fed by the same
// get_social_share_of_voice_timeseries rows. 'stream' = streamgraph (composition
// flow), 'rank' = bump chart (who overtook whom). Lazy-loaded so Nivo only ships
// when a customer actually opens one of these views.

interface SovRow {
  period: string;
  brand_id: string | null;
  brand_name: string | null;
  mentions: number | string | null;
  is_other: boolean | null;
}

const PALETTE = CHART.palette;
const OTHER_COLOR = CHART.other;
const N = (v: number | string | null | undefined) => (v == null ? 0 : Number(v) || 0);

// Collapse a hex toward a neutral dark slate, keeping only a faint tint — so
// non-focused bands read as a quiet backdrop, not a mix of colours.
function dimColor(hex: string): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  const W = 0.12; // fraction of the brand hue retained; the rest blends to slate
  const mix = (v: number, base: number) => Math.round(v * W + base * (1 - W));
  return `rgb(${mix(r, 42)},${mix(g, 48)},${mix(b, 58)})`;
}

function colorMap(rows: SovRow[], otherLabel: string): Record<string, string> {
  const totals = new Map<string, number>();
  for (const r of rows) {
    if (r.is_other || !r.brand_id) continue;
    const nm = r.brand_name ?? '—';
    totals.set(nm, (totals.get(nm) ?? 0) + N(r.mentions));
  }
  const ranked = [...totals.entries()].sort((a, b) => b[1] - a[1]);
  const map: Record<string, string> = { [otherLabel]: OTHER_COLOR };
  ranked.forEach(([nm], i) => { map[nm] = PALETTE[i % PALETTE.length]; });
  return map;
}

function periodsOf(rows: SovRow[]) {
  return Array.from(new Set(rows.map((r) => r.period))).sort();
}

export default function SovNivoCharts({
  rows,
  mode,
  otherLabel,
  locale,
  granularity,
  highlight,
  onBrandOpen,
  totalLabel = 'Σ',
}: {
  rows: SovRow[];
  mode: 'area' | 'share' | 'stream' | 'rank';
  otherLabel: string;
  locale: string;
  granularity: 'month' | 'week';
  highlight?: string | null;
  onBrandOpen?: (brandId: string, name: string) => void;
  totalLabel?: string;
}) {
  // Chart hover (area mode): which band the cursor is over + which period column +
  // cursor position for the floating breakdown card. Falls back to the legend-driven
  // `highlight` prop when the chart isn't hovered.
  const [selfHov, setSelfHov] = useState<string | null>(null);
  const [hoverP, setHoverP] = useState<number | null>(null);
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);
  const periods = periodsOf(rows);
  const cmap = colorMap(rows, otherLabel);
  const fmtMonth = (p: string) => new Date(p + 'T00:00:00').toLocaleDateString(locale, granularity === 'week' ? { day: 'numeric', month: 'short' } : { month: 'short' });
  // Keep ~8 x-labels regardless of granularity (52 weeks would otherwise collide).
  const labelStep = Math.max(1, Math.ceil(periods.length / 8));

  if (mode === 'area' || mode === 'share') {
    // Two views off one stacked-area engine.
    //
    // 'area'  — Volume: absolute mentions per period, INCLUDING the "other" long-tail so
    //           the period totals are real.
    // 'share' — Share of voice: the same bands normalised so every period sums to 100%.
    //
    // The share view is the one the panel's title actually promises, and it is the only
    // one that survives a change in OUR coverage. Volume conflates two different stories:
    // this category's chart climbs from near zero to ~1,300 mentions between February and
    // July, which reads as explosive market growth but is mostly us — more creators
    // tracked, deeper scraping, thousands of mentions backfilled. Normalising cancels that
    // out: if we start seeing twice as much of everything, the shares do not move, so what
    // is left is real movement between brands.
    const isShare = mode === 'share';
    const totals = new Map<string, number>();
    for (const r of rows) {
      const nm = r.is_other || !r.brand_id ? otherLabel : (r.brand_name ?? '—');
      totals.set(nm, (totals.get(nm) ?? 0) + N(r.mentions));
    }
    const keys = [...totals.entries()]
      .sort((a, b) => (a[0] === otherLabel ? 1 : b[0] === otherLabel ? -1 : b[1] - a[1]))
      .map((e) => e[0]);
    const perP = new Map<string, Record<string, number>>();
    for (const r of rows) {
      const nm = r.is_other || !r.brand_id ? otherLabel : (r.brand_name ?? '—');
      if (!perP.has(r.period)) perP.set(r.period, {});
      perP.get(r.period)![nm] = (perP.get(r.period)![nm] ?? 0) + N(r.mentions);
    }
    // Raw counts are kept alongside the plotted values: the share view still shows the
    // real mention count in its card, because "4.3%" alone hides whether that is 3
    // mentions or 300 — and in a thin month those are very different facts.
    const rawP = new Map<string, Record<string, number>>();
    for (const [p, rec] of perP) rawP.set(p, { ...rec });
    if (isShare) {
      for (const [p, rec] of perP) {
        const tot = Object.values(rec).reduce((s, v) => s + v, 0);
        if (tot > 0) for (const k of Object.keys(rec)) rec[k] = (rec[k] / tot) * 100;
      }
    }
    // brand name → id so clicking a band can open that brand's card (as it used to)
    const nameToId = new Map<string, string>();
    for (const r of rows) { if (!r.is_other && r.brand_id && r.brand_name && !nameToId.has(r.brand_name)) nameToId.set(r.brand_name, r.brand_id); }
    const series = keys.map((k) => ({
      id: k,
      data: periods.map((p) => ({ x: p, y: perP.get(p)?.[k] ?? 0 })),
    }));
    const defs = keys.flatMap((k, i) => {
      const c = cmap[k] ?? OTHER_COLOR;
      const dc = dimColor(c);
      return [
        // normal colourful view
        { id: 'sa' + i, type: 'linearGradient', x1: 0, y1: 0, x2: 0, y2: 1, colors: [{ offset: 0, color: c, opacity: 0.6 }, { offset: 100, color: c, opacity: 0.06 }] },
        // dimmed backdrop when another band is focused — OPAQUE so stacked bands don't
        // bleed through each other into a translucent mush
        { id: 'sad' + i, type: 'linearGradient', x1: 0, y1: 0, x2: 0, y2: 1, colors: [{ offset: 0, color: dc, opacity: 1 }, { offset: 100, color: dc, opacity: 0.92 }] },
        // vivid fill for the repainted (focused) band
        { id: 'sah' + i, type: 'linearGradient', x1: 0, y1: 0, x2: 0, y2: 1, colors: [{ offset: 0, color: c, opacity: 0.92 }, { offset: 100, color: c, opacity: 0.34 }] },
      ];
    });
    const ML = 34; // left margin (must match margin.left below) for cursor→period math
    // Effective highlight = the band the cursor is over OR the legend hover.
    const eff = selfHov ?? highlight ?? null;
    // When anything is highlighted, dim EVERY Nivo band; the highlighted one is repainted
    // opaque on top by the overlay below (so it's brought to the front, not colour-mixed).
    const fill = keys.map((k, i) => ({ match: { id: k }, id: eff ? 'sad' + i : 'sa' + i }));
    const clear = () => { setSelfHov(null); setHoverP(null); setCursor(null); };
    // Custom layer: transparent per-band hit-paths (which band?), a vertical crosshair at
    // the hovered period, and an opaque repaint of the highlighted band on top.
    const BandHover = (lp: {
      series: readonly { id: string | number; data: readonly { position: { x: number; y: number } }[] }[];
      innerHeight: number;
    }) => {
      const xs = lp.series[0]?.data.map((d) => d.position.x) ?? [];
      const nearestP = (clientX: number, svg: SVGSVGElement | null) => {
        if (!svg || xs.length === 0) return 0;
        const innerX = clientX - svg.getBoundingClientRect().left - ML;
        let best = 0, bd = Infinity;
        xs.forEach((px, i) => { const d = Math.abs(px - innerX); if (d < bd) { bd = d; best = i; } });
        return best;
      };
      // Match Nivo's monotoneX curve exactly, so hit-paths and the repaint sit ON the
      // curved bands — straight-line polygons would visibly diverge from the areas.
      const topPts = (s: { data: readonly { position: { x: number; y: number } }[] }) => s.data.map((d) => [d.position.x, d.position.y] as [number, number]);
      const basePts = (s: { data: readonly { position: { x: number; y: number } }[] }) => s.data.map((d) => [d.position.x, lp.innerHeight] as [number, number]);
      const curvedBand = (top: [number, number][], bot: [number, number][]) => {
        const botRev = [...bot].reverse();
        const tail = monotonePath(botRev).replace(/^M[^C]*/, '');
        return `${monotonePath(top)} L${botRev[0][0].toFixed(2)},${botRev[0][1].toFixed(2)}${tail} Z`;
      };
      const hi = eff ? lp.series.findIndex((s) => String(s.id) === eff) : -1;
      return (
        <g onMouseLeave={clear}>
          {lp.series.map((s, i) => {
            const id = String(s.id);
            return (
              <path
                key={id}
                d={curvedBand(topPts(s), i === 0 ? basePts(s) : topPts(lp.series[i - 1]))}
                fill="transparent"
                style={{ pointerEvents: 'all', cursor: nameToId.has(id) ? 'pointer' : 'default' }}
                onClick={() => { const bid = nameToId.get(id); if (bid && onBrandOpen) onBrandOpen(bid, id); }}
                onMouseEnter={() => setSelfHov(id)}
                onMouseMove={(e) => {
                  const idx = nearestP(e.clientX, e.currentTarget.ownerSVGElement);
                  setSelfHov((p) => (p === id ? p : id));
                  setHoverP((p) => (p === idx ? p : idx));
                  setCursor({ x: e.clientX, y: e.clientY });
                }}
              />
            );
          })}
          {/* vertical crosshair at the hovered period */}
          {hoverP != null && xs[hoverP] != null && (
            <line x1={xs[hoverP]} x2={xs[hoverP]} y1={0} y2={lp.innerHeight} stroke="#cfe0ff" strokeWidth={1.5} strokeOpacity={0.7} pointerEvents="none" />
          )}
          {/* highlighted band, repainted opaque on top (brought to front) */}
          {hi >= 0 && (() => {
            const s = lp.series[hi];
            const c = cmap[eff!] ?? OTHER_COLOR;
            const gi = keys.indexOf(eff!); // gradient id is keyed by `keys` order, not series order
            return (
              <g pointerEvents="none">
                <path d={curvedBand(topPts(s), hi === 0 ? basePts(s) : topPts(lp.series[hi - 1]))} fill={gi >= 0 ? `url(#sah${gi})` : c} fillOpacity={1} />
                <path d={monotonePath(topPts(s))} fill="none" stroke={c} strokeWidth={2.4} />
              </g>
            );
          })()}
        </g>
      );
    };
    // Focused card: the hovered brand's mentions this period + its share of the period
    // total (incl. "other"). That's the volume story — Поток covers per-brand composition.
    // Card numbers always come from the RAW counts, in both views: in share mode the
    // plotted values are percentages, so summing them would just report 100.
    const monthTotal = hoverP != null ? keys.reduce((s, k) => s + (rawP.get(periods[hoverP])?.[k] ?? 0), 0) : 0;
    const hoverVal = hoverP != null && eff ? (rawP.get(periods[hoverP])?.[eff] ?? 0) : 0;
    const sharePct = monthTotal > 0 ? (hoverVal / monthTotal) * 100 : 0;
    return (
      <div style={{ position: 'relative', height: 320 }}>
        <ResponsiveLine
          data={series}
          curve="monotoneX"
          colors={(s: { id: string | number }) => { const k = String(s.id); const c = cmap[k] ?? OTHER_COLOR; return eff && eff !== k ? dimColor(c) : c; }}
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', min: 0, max: isShare ? 100 : 'auto', stacked: true }}
          enableArea
          areaOpacity={eff ? 1 : 0.9}
          defs={defs}
          fill={fill}
          lineWidth={1.4}
          enablePoints={false}
          enableGridX={false}
          gridYValues={4}
          margin={{ top: 10, right: 12, bottom: 26, left: ML }}
          axisLeft={isShare
            ? { tickSize: 0, tickPadding: 6, tickValues: [0, 25, 50, 75, 100], format: (v: number) => `${v}%` }
            : { tickSize: 0, tickPadding: 6, tickValues: 4 }}
          axisBottom={{ tickSize: 0, tickPadding: 8, tickValues: periods.filter((_, i) => i % labelStep === 0), format: (p) => fmtMonth(String(p)) }}
          enableSlices={false}
          layers={['grid', 'axes', 'areas', 'lines', BandHover]}
          theme={nivoTheme}
          animate={false}
        />
        {hoverP != null && cursor && (
          <div style={{ position: 'fixed', left: cursor.x + 16, top: cursor.y - 12, pointerEvents: 'none', zIndex: 30, background: '#161a24', color: '#dde', fontSize: 12, borderRadius: 8, border: '1px solid rgba(74,158,255,0.3)', padding: '8px 11px', boxShadow: '0 10px 32px rgba(0,0,0,0.5)', whiteSpace: 'nowrap' }}>
            <div style={{ color: '#8a94a6', fontSize: 11, marginBottom: 3 }}>{fmtMonth(periods[hoverP])}</div>
            <div><span style={{ color: '#8a94a6', fontSize: 11, marginRight: 4 }}>{totalLabel}</span><b style={{ color: '#fff', fontSize: 16 }}>{monthTotal.toLocaleString(i18nInst.language)}</b></div>
            {eff && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, color: '#c7cfdb' }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: cmap[eff] ?? OTHER_COLOR, display: 'inline-block' }} />
                <span>{eff}</span>
                <span style={{ color: '#8a94a6' }}>· {hoverVal.toLocaleString(i18nInst.language)} · {sharePct.toFixed(1)}%</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (mode === 'stream') {
    // Named brands only — the "other" long-tail swamps the stream and buries the
    // brands the customer actually cares about (it's in the Volume view instead).
    const totals = new Map<string, number>();
    for (const r of rows) {
      if (r.is_other || !r.brand_id) continue;
      const nm = r.brand_name ?? '—';
      totals.set(nm, (totals.get(nm) ?? 0) + N(r.mentions));
    }
    const keys = [...totals.entries()].sort((a, b) => b[1] - a[1]).map((e) => e[0]);
    const perP = new Map<string, Record<string, number>>();
    for (const r of rows) {
      if (r.is_other || !r.brand_id) continue;
      const nm = r.brand_name ?? '—';
      if (!perP.has(r.period)) perP.set(r.period, {});
      perP.get(r.period)![nm] = (perP.get(r.period)![nm] ?? 0) + N(r.mentions);
    }
    const data = periods.map((p) => {
      const o: Record<string, number> = {};
      keys.forEach((k) => { o[k] = perP.get(p)?.[k] ?? 0; });
      return o;
    });
    return (
      <div style={{ height: 320 }}>
        <ResponsiveStream
          data={data}
          keys={keys}
          offsetType="silhouette"
          order="insideOut"
          colors={(layer: { id: string | number }) => { const c = cmap[String(layer.id)] ?? OTHER_COLOR; return highlight && highlight !== String(layer.id) ? dimColor(c) : c; }}
          fillOpacity={0.85}
          borderWidth={0.5}
          borderColor={{ from: 'color', modifiers: [['darker', 0.4]] }}
          margin={{ top: 10, right: 12, bottom: 26, left: 12 }}
          axisLeft={null}
          axisBottom={{ format: (i: number) => (i % labelStep === 0 ? fmtMonth(periods[i] ?? '') : ''), tickSize: 0, tickPadding: 8 }}
          enableGridX={false}
          enableGridY={false}
          theme={nivoTheme}
          isInteractive
          animate
        />
      </div>
    );
  }

  // rank / bump — named brands only (rank race is about the players, not "other")
  const totals = new Map<string, number>();
  for (const r of rows) {
    if (r.is_other || !r.brand_id) continue;
    totals.set(r.brand_name ?? '—', (totals.get(r.brand_name ?? '—') ?? 0) + N(r.mentions));
  }
  const top = [...totals.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6).map((e) => e[0]);
  const perP = new Map<string, Record<string, number>>();
  for (const r of rows) {
    if (r.is_other || !r.brand_id) continue;
    const nm = r.brand_name ?? '—';
    if (!top.includes(nm)) continue;
    if (!perP.has(r.period)) perP.set(r.period, {});
    perP.get(r.period)![nm] = (perP.get(r.period)![nm] ?? 0) + N(r.mentions);
  }
  // Bump wants a RANK in `y`, not a magnitude: it draws y=1 at the top and counts
  // downward. Feeding it raw mention counts inverted the whole chart — the category
  // leader (most mentions → largest y) sank to the bottom while the quietest brand
  // floated on top. Rank each period explicitly instead, 1 = most mentions.
  // Ties share the lower number so two brands level on 40 mentions both read as 3rd.
  const rankPerP = new Map<string, Record<string, number>>();
  for (const p of periods) {
    const vals = top
      .map((nm) => ({ nm, v: perP.get(p)?.[nm] ?? 0 }))
      .sort((a, b) => b.v - a.v);
    const r: Record<string, number> = {};
    vals.forEach((e, i) => {
      const prev = i > 0 ? vals[i - 1] : null;
      r[e.nm] = prev && prev.v === e.v ? r[prev.nm] : i + 1;
    });
    rankPerP.set(p, r);
  }
  const series = top.map((nm) => ({
    id: nm,
    data: periods.map((p) => ({ x: fmtMonth(p), y: rankPerP.get(p)?.[nm] ?? top.length })),
  }));
  return (
    <div style={{ height: 320 }}>
      <ResponsiveBump
        data={series}
        colors={(s: { id: string }) => { const c = cmap[s.id] ?? '#888'; return highlight && highlight !== s.id ? dimColor(c) : c; }}
        lineWidth={2}
        activeLineWidth={4}
        inactiveLineWidth={1}
        inactiveOpacity={0.2}
        pointSize={7}
        activePointSize={11}
        inactivePointSize={4}
        pointBorderWidth={2}
        pointBorderColor={{ from: 'serie.color' }}
        margin={{ top: 14, right: 16, bottom: 26, left: 30 }}
        // Without a numbered axis the chart shows lines crossing but never says what
        // the height means — and a reader has no way to notice that 1st place is on top.
        axisLeft={{ tickSize: 0, tickPadding: 6,
                    tickValues: Array.from({ length: top.length }, (_, i) => i + 1),
                    format: (v: number) => String(v) }}
        axisRight={null}
        axisBottom={{ tickSize: 0, tickPadding: 8, tickValues: periods.filter((_, i) => i % labelStep === 0).map(fmtMonth) }}
        startLabel={false}
        endLabel={false}
        theme={nivoTheme}
      />
    </div>
  );
}
